import React from 'react';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import formatter from '@payit/payit-formatter';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Confirm from '../../confirm/Confirm';
import VerticalLabelList from '../../label/VerticalLabelList';
import FieldSet from '../../fieldset/FieldSet';
import ObjectUtils from '../../../modules/ObjectUtils';
import Grid from '../../grid/Grid';
import PaymentGatewayService from '../../../service/PaymentGatewayService';

class DetailedScheduledInstallmentPlan extends React.Component {
  constructor(props) {
    super(props);
    const { scheduledInstallmentPlan, } = props;
    this.state = {
      scheduledInstallmentPlan,
      isReady: false,
      isLoading: false,
      cancelOpen: false,
    };

    this.cancelInstallmentPlan = this.cancelInstallmentPlan.bind(this);
    this.handleHide = this.handleHide.bind(this);
    this.paymentKeyExclusions = [ 'cart', 'cartPaymentRequest', 'customerId', 'managedPay', 'notificationConfig', ];
  }

  componentDidMount() {
    const { scheduledInstallmentPlan, } = this.props;
    this.clearNullValues(scheduledInstallmentPlan);
  }

  handleCancelClickOpen = () => {
    this.setState({ cancelOpen: true, });
  };

  handleCancelClose = () => {
    this.setState({ cancelOpen: false, });
  };

  handleHide() {
    this.setState({ status: '', });
  }

  clearNullValues(scheduledInstallmentPlan) {
    const iterableObject = scheduledInstallmentPlan;
    Object.keys(iterableObject).forEach((key) => {
      if (iterableObject[key] === null) {
        iterableObject[key] = '';
      }

      if (typeof iterableObject[key] === 'object') {
        this.clearNullValues(iterableObject[key]);
      }
    });

    this.setState({
      scheduledInstallmentPlan: iterableObject,
      isReady: true,
    });
  }

  cancelInstallmentPlan(scheduledInstallmentPlanId) {
    this.setState({ isLoading: true, });
    function cancel() {
      return PaymentGatewayService.managedpay.cancelInstallmentPlan({ scheduledInstallmentPlanId, });
    }
    cancel.call()
      .then(() => {
        this.setState({
          status: (
            <Alert className="mt-2" variant="success">
              <Alert.Heading>Success!</Alert.Heading>
              <p>{`Installment plan ${scheduledInstallmentPlanId} has been cancelled!`}</p>
              <div className="d-flex justify-content-end">
                <Button onClick={this.handleHide} variant="outline-success">
                  OK
                </Button>
              </div>
            </Alert>
          ),
        });
      })
      .catch(() => {
        this.setState({
          status: (
            <Alert className="mt-2" variant="danger">
              <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
              <p>{`Unable to cancel installment plan ${scheduledInstallmentPlanId}`}</p>
              <div className="d-flex justify-content-end">
                <Button onClick={this.handleHide} variant="outline-danger">
                  OK
                </Button>
              </div>
            </Alert>
          ),
        });
      })
      .finally(() => {
        this.handleCancelClose();
        this.setState({ isLoading: false, });
      });
  }

  getServiceOfferingId(installmentPlan) {
    try {
      return installmentPlan.scheduledCarts[0].cart.subCarts[0].payables[0].serviceOfferingId;
    } catch (err) {
      console.error(err);
      return '-';
    }
  }

  getPaymentAmount(payment) {
    const amount = payment.cart.subCarts[0].payables[0].amount;
    return formatter.money.printMoney(amount);
  }

  getPaymentAccount(payment) {
    return payment.cartPaymentRequest.subPayments[0].paymentAccountToken;
  }

  getPaymentDate(payment) {
    const date = new Date(payment.cartPaymentRequest.paymentDate);
    return date.toDateString();
  }

  getPaymentDates(installmentPlan) {
    try {
      const dates = installmentPlan.scheduledCarts
        .map((scheduledCart) => new Date(scheduledCart.cartPaymentRequest.paymentDate))
        .sort((a, b) => b - a);
      return dates;
    } catch (err) {
      console.error(err);
      return '-';
    }
  }

  renderScheduledPayments(scheduledPayments) {
    const formattedPayments = JSON.parse(JSON.stringify(scheduledPayments));
    const paymentRows = [];

    formattedPayments.forEach((payment) => {
      const item = payment;
      item.scheduledDate = this.getPaymentDate(payment);
      item.amount = this.getPaymentAmount(payment);
      item.status = payment.status;
      item.paymentAccount = this.getPaymentAccount(payment);

      this.paymentKeyExclusions.forEach(key => {
        delete item[key];
      });
      return item;
    });

    formattedPayments.forEach(payment => {
      const values = Object.values(payment);
      paymentRows.push({
        value: values,
        raw: payment,
      });
    });

    const paymentHeaders = ObjectUtils.toLargestKeySet(formattedPayments).map(payment => ({
      display: lodash.startCase(payment),
      value: payment,
    }));

    return (
      <FieldSet title="Payments">
        <Grid headers={paymentHeaders} rows={paymentRows} />
      </FieldSet>
    );
  }

  renderSummary(scheduledInstallmentPlan) {
    const exclusions = [];
    const dueDates = this.getPaymentDates(scheduledInstallmentPlan);

    const items = VerticalLabelList.toList({
      'Service Offering ID': this.getServiceOfferingId(scheduledInstallmentPlan),
      'Customer ID': scheduledInstallmentPlan.customerId,
      'Bill Amount': formatter.money.printMoney(scheduledInstallmentPlan.subTotal),
      'Amount Remaining': formatter.money.printMoney(scheduledInstallmentPlan.balanceRemaining),
      'Start Date': dueDates[dueDates.length - 1].toDateString(),
      'End Date': dueDates[0].toDateString(),
    });

    return (
      <FieldSet title="Installment Plan" style={{ maxHeight: '400px', }}>
        <VerticalLabelList items={items} exclusions={exclusions} />
      </FieldSet>
    );
  }

  renderCancelButton(isLoading, status, cancelOpen, scheduledInstallmentPlan) {
    let button = '';
    scheduledInstallmentPlan.scheduledCarts.forEach((payment) => {
      if (payment.status === 'PENDING') {
        button = (
          <>
            <hr />
            <Button variant="primary" className="small" onClick={this.handleCancelClickOpen}>
              Cancel Plan
            </Button>
            <Confirm
              isLoading={isLoading}
              title="Cancel Scheduled Installment Plan Confirmation"
              content="Are you sure you want to cancel this installment plan?"
              open={cancelOpen}
              onClose={this.handleCancelClose}
              onYes={() => this.cancelInstallmentPlan(scheduledInstallmentPlan.scheduledInstallmentPlanId)}
              onNo={this.handleCancelClose}
            />
            {status}
          </>
        );
      }
    });
    return button;
  }

  render() {
    const {
      isReady,
      isLoading,
      status,
      cancelOpen,
      scheduledInstallmentPlan,
    } = this.state;

    return (
      <>
        {(() => {
          if (isReady) {
            return (
              <>
                {this.renderSummary(scheduledInstallmentPlan)}
                {this.renderScheduledPayments(scheduledInstallmentPlan.scheduledCarts)}
                {this.renderCancelButton(isLoading, status, cancelOpen, scheduledInstallmentPlan)}
              </>
            );
          }
          return undefined;
        })()}
      </>
    );
  }
}

DetailedScheduledInstallmentPlan.propTypes = {
  scheduledInstallmentPlan: PropTypes.instanceOf(Object).isRequired,
};

export default DetailedScheduledInstallmentPlan;
